/// Phone / tablet: left-right halves with a swap, matching the native app.

import { useTranslation } from 'react-i18next';

import { useSettingsStore, useThemeColors } from '@/settings/handler/settingsStore';
import { PixelButton } from '@/ui/pixel/PixelButton';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelIcon } from '@/ui/pixel/PixelIcon';

export function TouchLayoutEditor() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const swapped = useSettingsStore((s) => s.controlsSwapped);
  const setControlsSwapped = useSettingsStore((s) => s.setControlsSwapped);

  const leftAction = swapped ? t('controlsJump') : t('controlsDuck');
  const rightAction = swapped ? t('controlsDuck') : t('controlsJump');
  const leftIcon = swapped ? 'arrow_up' : 'arrow_down';
  const rightIcon = swapped ? 'arrow_down' : 'arrow_up';

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex items-center gap-2.5">
        <TouchCard action={leftAction} detail={t('controlsLeftSide')} icon={leftIcon} />
        <PixelButton
          ariaLabel={t('controlsSwap')}
          onClick={() => setControlsSwapped(!swapped)}
          icon={
            <div className="flex flex-col items-center gap-0.5 px-1.5 py-2">
              <PixelIcon name="arrow_right" size={16} color={colors.onSurface} />
              <span className="leading-none" style={{ fontSize: 14, color: colors.onSurface }}>
                {t('controlsSwap')}
              </span>
              <PixelIcon name="arrow_left" size={16} color={colors.onSurface} />
            </div>
          }
        />
        <TouchCard action={rightAction} detail={t('controlsRightSide')} icon={rightIcon} />
      </div>
      <p className="text-center leading-tight" style={{ fontSize: 18, color: colors.onSurface, opacity: 0.75 }}>
        {t('controlsHint')}
      </p>
    </div>
  );
}

export function TouchPreview({ className = '' }: { className?: string }) {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const swapped = useSettingsStore((s) => s.controlsSwapped);
  const setControlsSwapped = useSettingsStore((s) => s.setControlsSwapped);
  const leftAction = swapped ? t('controlsJump') : t('controlsDuck');
  const rightAction = swapped ? t('controlsDuck') : t('controlsJump');
  const leftIcon = swapped ? 'arrow_up' : 'arrow_down';
  const rightIcon = swapped ? 'arrow_down' : 'arrow_up';

  return (
    <div className={className}>
      <PixelContainer
        radius={3}
        pixelScale={2}
        padding="14px"
        backgroundColor={colors.surfaceContainerHighest}
        borderColor={colors.onSurface}
      >
        <div className="aspect-video">
          <PixelContainer
            radius={2}
            pixelScale={2}
            padding="0"
            backgroundColor={colors.surfaceContainerHigh}
            borderColor={`${colors.onSurface}8C`}
          >
            <div className="flex h-full min-h-[140px]">
              <HalfPreview action={leftAction} sideLabel={t('controlsLeftSide')} icon={leftIcon} />
              <div className="w-0.5 shrink-0" style={{ backgroundColor: colors.onSurface, opacity: 0.35 }} />
              <HalfPreview action={rightAction} sideLabel={t('controlsRightSide')} icon={rightIcon} />
            </div>
          </PixelContainer>
        </div>
        <div className="mt-3.5 flex justify-center">
          <PixelContainer
            radius={9}
            pixelScale={2}
            padding="12px 18px"
            backgroundColor={colors.surfaceContainerHigh}
            borderColor={colors.onSurface}
            onClick={() => setControlsSwapped(!swapped)}
          >
            <div className="flex items-center justify-center">
              <PixelIcon name="arrow_left" size={14} color={colors.onSurface} />
              <span className="mx-2 leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                {t('controlsSwap')}
              </span>
              <PixelIcon name="arrow_right" size={14} color={colors.onSurface} />
            </div>
          </PixelContainer>
        </div>
      </PixelContainer>
      <p className="mt-3.5" style={{ fontSize: 16, lineHeight: 1.25, color: colors.onSurface, opacity: 0.7 }}>
        {t('controlsHint')}
      </p>
    </div>
  );
}

function TouchCard({ action, detail, icon }: { action: string; detail: string; icon: string }) {
  const colors = useThemeColors();
  return (
    <div className="min-w-0 flex-1">
      <PixelContainer
        radius={2}
        pixelScale={2}
        padding="14px 10px"
        backgroundColor={colors.surfaceContainerHigh}
        borderColor={colors.onSurface}
        className="w-full opacity-[0.95]"
      >
        <div className="flex flex-col items-center gap-1.5">
          <div className="flex items-center justify-center gap-1.5">
            <PixelIcon name={icon} size={18} color={colors.onSurface} />
            <span className="truncate leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
              {action}
            </span>
          </div>
          <span className="text-center leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.65 }}>
            {detail}
          </span>
        </div>
      </PixelContainer>
    </div>
  );
}

function HalfPreview({
  action,
  sideLabel,
  icon,
}: {
  action: string;
  sideLabel: string;
  icon: string;
}) {
  const colors = useThemeColors();
  return (
    <div className="flex flex-1 flex-col items-center justify-center px-3 py-4">
      <div className="flex items-center justify-center">
        <PixelIcon name={icon} size={18} color={colors.onSurface} />
        <span className="ml-1.5 truncate leading-none" style={{ fontSize: 22, color: colors.onSurface }}>
          {action}
        </span>
      </div>
      <span className="mt-2 text-center leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.55 }}>
        {sideLabel}
      </span>
    </div>
  );
}
